/**
 * Modal for a calendar event (list or details).
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarEvent, type CalendarParticipant } from '@/types/calendar.types';
import { InfoModal } from '@/components/common/modals/InfoModal';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Tag,
  Star,
  BookOpen,
  Building2,
  User,
  GraduationCap,
  ArrowLeft,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getRoleLabel } from '@/utils/role-label-utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { EVENT_TYPE_CONFIG } from '@/components/calendar/calendar-configs';
import {
  getCalendarSessionParticipantColumns,
  getFieldEventStatusChip,
  getHolidayTypeLabel,
  getPostitStatusChip,
  getThematicLabel,
  getSessionStatusChip,
  resolveCalendarSessionTypeConfig,
  sortCalendarEvents,
  splitFieldEventParticipants,
  useEventTypeIcons,
} from '@/components/calendar/calendar-utils';
import { ESTABLISHMENT_TYPE_CONFIG } from '@/constants/shared-catalog';
import { useMode } from '@/contexts/ModeContext';

/** Calendar event modal props. */
interface CalendarEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: CalendarEvent | null;
  dayEvents?: CalendarEvent[];
  onEdit?: (sourceId: string, type: string) => void;
}

/** Calendar event modal: day list or type-specific details, with back-stack and deep-links. */
export const CalendarEventModal: React.FC<CalendarEventModalProps> = ({
  isOpen,
  onClose,
  event,
  dayEvents,
  onEdit,
}) => {
  const navigate = useNavigate();
  const { mode } = useMode();
  const showFieldEvents = mode !== 'remote';
  const typeIcons = useEventTypeIcons();
  const [internalView, setInternalView] = React.useState<'list' | 'details'>('list');
  const [selectedEvent, setSelectedEvent] = React.useState<CalendarEvent | null>(event);
  const [canGoBack, setCanGoBack] = React.useState(false);

  /** List view when several events share a day; details when there is a single event. */
  React.useEffect(() => {
    if (!isOpen) return;

    if (dayEvents?.length && dayEvents.length > 1) {
      setInternalView('list');
      setSelectedEvent(null);
      setCanGoBack(false);
    } else if (dayEvents?.length === 1) {
      setInternalView('details');
      setSelectedEvent(dayEvents[0]);
      setCanGoBack(false);
    } else if (event) {
      setInternalView('details');
      setSelectedEvent(event);
      setCanGoBack(false);
    }
  }, [isOpen, event, dayEvents]);

  /** Reset list / details / back-stack when the modal closes. */
  React.useEffect(() => {
    if (!isOpen) {
      setInternalView('list');
      setSelectedEvent(null);
      setCanGoBack(false);
    }
  }, [isOpen]);

  /** Drill from the day list into one event; enable the back button. */
  const handleEventSelect = (evt: CalendarEvent) => {
    setSelectedEvent(evt);
    setInternalView('details');
    setCanGoBack(true);
  };

  const handleBack = () => {
    setInternalView('list');
    setSelectedEvent(null);
  };

  if (!event && !dayEvents) return null;

  const getEventTypeIcon = (evt: CalendarEvent) => typeIcons[evt.type] ?? typeIcons.session;

  if (internalView === 'list' && dayEvents) {
    return (
      <InfoModal
        open={isOpen}
        onClose={onClose}
        size="lg"
        icon={Calendar}
        iconColor="var(--color-2-nuance-1)"
        iconBgColor="color-mix(in srgb, var(--color-2-nuance-1) 15%, transparent)"
        iconSize="lg"
        title={`Événements du ${format(dayEvents[0]?.startDate || new Date(), 'd MMMM', { locale: fr })}`}
        titleColor="var(--foreground)"
        titleSize="xl"
        actions={[
          {
            id: 'close',
            label: 'Fermer',
            onClick: onClose,
            preset: 'modal-cancel' as const,
          },
        ]}
      >
        <div className="space-y-3">
          {sortCalendarEvents(dayEvents).map((evt) => {
            const config = EVENT_TYPE_CONFIG[evt.type];
            const Icon = typeIcons[evt.type] ?? typeIcons.session;

            return (
              <button
                key={evt.id}
                type="button"
                onClick={() => handleEventSelect(evt)}
                className="calendar-event-modal-list-item"
              >
                <div
                  className={cn(
                    'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
                    config.bgClass,
                  )}
                >
                  <Icon className={cn('w-5 h-5', config.textClass)} />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="font-semibold text-foreground">{evt.title}</p>
                  <div className="flex items-center gap-2 text-sm text-foreground mt-1 flex-wrap">
                    <LabelBadge
                      label={config.label}
                      icon={Icon}
                      variant="outlined"
                      color={config.color}
                      className="min-w-0"
                    />
                    {!evt.allDay && (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {format(evt.startDate, 'HH:mm')} - {format(evt.endDate, 'HH:mm')}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </InfoModal>
    );
  }

  const currentEvent = selectedEvent || event;
  if (!currentEvent) return null;

  const EventTypeIcon = getEventTypeIcon(currentEvent);

  /** Session body: type / status badges, venue, program chips, split participant lists. */
  const renderSessionDetails = () => {
    const typeConfig = resolveCalendarSessionTypeConfig(
      currentEvent.metadata.sessionType,
      currentEvent.metadata.sessionFormat,
      mode,
    );
    const statusConfig = getSessionStatusChip(
      currentEvent.metadata.sessionType,
      currentEvent.metadata.sessionStatus,
    );
    const TypeIcon = typeConfig?.icon;
    const StatusIcon = statusConfig.icon;

    const estabTypeConfig = currentEvent.metadata.establishmentType
      ? ESTABLISHMENT_TYPE_CONFIG[currentEvent.metadata.establishmentType]
      : undefined;

    const levelNames = currentEvent.metadata.levelNames || [];
    const subjectNames = currentEvent.metadata.subjectNames || [];
    const programName = currentEvent.metadata.programName;

    const participants = currentEvent.metadata.participants || [];
    const participantColumns = getCalendarSessionParticipantColumns(
      participants,
      currentEvent.metadata.sessionType,
      currentEvent.metadata.sessionFormat,
      mode,
    );
    const { leftLabel, rightLabel, left, right } = participantColumns;
    const showRightColumn = rightLabel !== null;

    function renderLeftColumnRow(p: CalendarParticipant): React.ReactNode {
      return (
        <li key={`tsc-${p.name}`} className="flex items-center gap-1.5 min-w-0">
          <div
            className="flex items-center justify-center w-6 h-6 rounded-md shrink-0"
            style={{
              background: 'color-mix(in srgb, var(--color-2-nuance-1) 12%, transparent)',
            }}
          >
            <User className="w-3 h-3 text-[var(--color-2-nuance-1)]" />
          </div>
          <span className="text-sm text-foreground truncate">{p.name}</span>
          {p.role === 'volunteer' ? (
            <LabelBadge
              label={getRoleLabel('volunteer')}
              variant="outlined"
              color="var(--color-3-nuance-1)"
              className="min-w-0 shrink-0"
            />
          ) : null}
        </li>
      );
    }

    function renderRightColumnRow(p: CalendarParticipant): React.ReactNode {
      return (
        <li key={`stu-${p.name}`} className="flex items-center gap-1.5 min-w-0">
          <div
            className="flex items-center justify-center w-6 h-6 rounded-md shrink-0"
            style={{
              background: 'color-mix(in srgb, var(--color-3-nuance-1) 12%, transparent)',
            }}
          >
            <User className="w-3 h-3 text-[var(--color-3-nuance-1)]" />
          </div>
          <span className="text-sm text-foreground truncate flex-1 min-w-0">{p.name}</span>
          {p.levelName ? (
            <LabelBadge
              label={p.levelName}
              variant="outlined"
              color="var(--color-1-nuance-1)"
              className="min-w-0 shrink-0"
            />
          ) : null}
        </li>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {typeConfig &&
            TypeIcon &&
            (currentEvent.metadata.sessionType === 'whole_class' ? (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <LabelBadge
                        label={typeConfig.label}
                        icon={TypeIcon as LucideIcon}
                        variant="outlined"
                        color={typeConfig.badgeColor}
                        className="min-w-0"
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>1 compte rendu généré pour cette séance</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <LabelBadge
                label={typeConfig.label}
                icon={TypeIcon as LucideIcon}
                variant="outlined"
                color={typeConfig.badgeColor}
                className="min-w-0"
              />
            ))}
          <LabelBadge
            label={statusConfig.label}
            icon={StatusIcon as LucideIcon}
            variant="outlined"
            color="var(--color-2-nuance-2)"
            className="min-w-0"
          />
        </div>

        <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border calendar-event-modal-info-box-c2">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg calendar-event-modal-icon-box-c2-n1 shrink-0">
            <Calendar className="w-4 h-4" />
          </div>
          <div>
            <p className="font-semibold text-foreground capitalize text-sm">
              {format(currentEvent.startDate, 'EEEE d MMMM yyyy', { locale: fr })}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5 text-muted-foreground text-sm">
              <Clock className="w-3.5 h-3.5" />
              <span>
                {format(currentEvent.startDate, 'HH:mm')} – {format(currentEvent.endDate, 'HH:mm')}
              </span>
            </div>
          </div>
        </div>

        {currentEvent.metadata.establishmentName && (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border calendar-event-modal-info-box-c2">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg calendar-event-modal-icon-box-c2-n2 shrink-0">
              <Building2 className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-foreground text-sm">
                  {currentEvent.metadata.establishmentName}
                </p>
                {estabTypeConfig && (
                  <LabelBadge
                    label={estabTypeConfig.label}
                    icon={estabTypeConfig.icon}
                    variant="outlined"
                    color={estabTypeConfig.badgeColor}
                    className="min-w-0"
                  />
                )}
              </div>
              {currentEvent.metadata.roomName && (
                <div className="flex items-center gap-1.5 mt-0.5 text-muted-foreground text-sm">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>{currentEvent.metadata.roomName}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {(programName || levelNames.length > 0 || subjectNames.length > 0) && (
          <div className="flex flex-wrap gap-1.5">
            {programName && (
              <LabelBadge
                label={programName}
                icon={BookOpen}
                variant="outlined"
                color="var(--color-2-nuance-1)"
                className="min-w-0"
              />
            )}
            {levelNames.map((name) => (
              <LabelBadge
                key={name}
                label={name}
                icon={GraduationCap}
                variant="outlined"
                color="var(--color-1-nuance-1)"
                className="min-w-0"
              />
            ))}
            {subjectNames.map((name) => (
              <LabelBadge
                key={name}
                label={name}
                icon={BookOpen}
                variant="outlined"
                color="var(--color-3-nuance-1)"
                className="min-w-0"
              />
            ))}
          </div>
        )}

        {participants.length > 0 && (
          <div className="rounded-xl border border-[var(--color-2-nuance-1)]/20 overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 bg-[color-mix(in_srgb,var(--color-2-nuance-1)_5%,transparent)] border-b border-[var(--color-2-nuance-1)]/15">
              <Users className="w-4 h-4 text-[var(--color-2-nuance-1)]" />
              <span className="text-xs font-semibold text-[var(--color-2-nuance-1)] uppercase tracking-wide">
                Participants
              </span>
            </div>
            <div
              className={
                showRightColumn
                  ? 'grid grid-cols-2 divide-x divide-[var(--color-2-nuance-1)]/10'
                  : undefined
              }
            >
              <div className="p-3">
                <p className="text-xs font-semibold text-[var(--color-2-nuance-1)] mb-2 flex items-center gap-1">
                  <GraduationCap className="w-3.5 h-3.5" />
                  {leftLabel}
                  {left.length > 0 && (
                    <span className="ml-auto text-[10px] bg-[color-mix(in_srgb,var(--color-2-nuance-1)_15%,transparent)] text-[var(--color-2-nuance-1)] rounded-full px-1.5 py-0.5 font-bold">
                      {left.length}
                    </span>
                  )}
                </p>
                {left.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Aucun</p>
                ) : (
                  <ul className="space-y-1.5">{left.map(renderLeftColumnRow)}</ul>
                )}
              </div>

              {showRightColumn ? (
                <div className="p-3">
                  <p className="text-xs font-semibold text-[var(--color-3-nuance-1)] mb-2 flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />
                    {rightLabel}
                    {right.length > 0 && (
                      <span className="ml-auto text-[10px] bg-[color-mix(in_srgb,var(--color-3-nuance-1)_15%,transparent)] text-[var(--color-3-nuance-1)] rounded-full px-1.5 py-0.5 font-bold">
                        {right.length}
                      </span>
                    )}
                  </p>
                  {right.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">Aucun</p>
                  ) : (
                    <ul className="space-y-1.5">{right.map(renderRightColumnRow)}</ul>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}

        {currentEvent.description && (
          <div className="p-3 rounded-xl bg-muted/20 border calendar-event-modal-section-border-c2">
            <p className="text-xs font-medium mb-1.5 calendar-event-modal-section-label-c2">
              Notes internes
            </p>
            <p className="text-sm text-foreground">{currentEvent.description}</p>
          </div>
        )}
      </div>
    );
  };

  /** Field-event body: status, activity, venue, organizers vs students (+ unregistered count). */
  const renderEventDetails = () => {
    const statusConfig = getFieldEventStatusChip(currentEvent.metadata.eventStatus);
    const StatusIcon = statusConfig.icon;

    const participants = currentEvent.metadata.participants || [];
    const { organizers, students } = splitFieldEventParticipants(participants);
    const unregisteredCount = currentEvent.metadata.unregisteredStudentCount ?? 0;

    const estabTypeConfig = currentEvent.metadata.establishmentType
      ? ESTABLISHMENT_TYPE_CONFIG[currentEvent.metadata.establishmentType]
      : undefined;

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <LabelBadge
            label={statusConfig.label}
            icon={StatusIcon as LucideIcon}
            variant="outlined"
            color="var(--color-4-nuance-1)"
            className="min-w-0"
          />
          {currentEvent.metadata.activityTypeName && (
            <LabelBadge
              label={currentEvent.metadata.activityTypeName}
              icon={Tag}
              variant="outlined"
              color="var(--color-4-nuance-2)"
              className="min-w-0"
            />
          )}
        </div>

        <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border calendar-event-modal-info-box-c4">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg calendar-event-modal-icon-box-c4-n1 shrink-0">
            <Calendar className="w-4 h-4" />
          </div>
          <div>
            <p className="font-semibold text-foreground capitalize text-sm">
              {format(currentEvent.startDate, 'EEEE d MMMM yyyy', { locale: fr })}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5 text-muted-foreground text-sm">
              <Clock className="w-3.5 h-3.5" />
              <span>
                {format(currentEvent.startDate, 'HH:mm')} – {format(currentEvent.endDate, 'HH:mm')}
              </span>
            </div>
          </div>
        </div>

        {currentEvent.metadata.activityName && (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border calendar-event-modal-info-box-c4">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg calendar-event-modal-icon-box-c4-n2 shrink-0">
              <Sparkles className="w-4 h-4" />
            </div>
            <p className="font-semibold text-foreground text-sm">
              {currentEvent.metadata.activityName}
            </p>
          </div>
        )}

        {currentEvent.metadata.establishmentName && (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border calendar-event-modal-info-box-c4">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg calendar-event-modal-icon-box-c4-n1 shrink-0">
              <Building2 className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-foreground text-sm">
                  {currentEvent.metadata.establishmentName}
                </p>
                {estabTypeConfig && (
                  <LabelBadge
                    label={estabTypeConfig.label}
                    icon={estabTypeConfig.icon}
                    variant="outlined"
                    color={estabTypeConfig.badgeColor}
                    className="min-w-0"
                  />
                )}
              </div>
              {currentEvent.metadata.roomName && (
                <div className="flex items-center gap-1.5 mt-0.5 text-muted-foreground text-sm">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>{currentEvent.metadata.roomName}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {(organizers.length > 0 || students.length > 0 || unregisteredCount > 0) && (
          <div className="rounded-xl border border-[var(--color-4-nuance-1)]/20 overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 bg-[color-mix(in_srgb,var(--color-4-nuance-1)_5%,transparent)] border-b border-[var(--color-4-nuance-1)]/15">
              <Users className="w-4 h-4 text-[var(--color-4-nuance-1)]" />
              <span className="text-xs font-semibold text-[var(--color-4-nuance-1)] uppercase tracking-wide">
                Participants
              </span>
            </div>
            <div className="grid grid-cols-2 divide-x divide-[var(--color-4-nuance-1)]/10">
              <div className="p-3">
                <p className="text-xs font-semibold text-[var(--color-4-nuance-1)] mb-2 flex items-center gap-1">
                  <GraduationCap className="w-3.5 h-3.5" />
                  Organisateurs
                  {organizers.length > 0 && (
                    <span className="ml-auto text-[10px] bg-[color-mix(in_srgb,var(--color-4-nuance-1)_15%,transparent)] text-[var(--color-4-nuance-1)] rounded-full px-1.5 py-0.5 font-bold">
                      {organizers.length}
                    </span>
                  )}
                </p>
                {organizers.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Aucun</p>
                ) : (
                  <ul className="space-y-1.5">
                    {organizers.map((p) => (
                      <li key={`org-${p.name}`} className="flex items-center gap-1.5 min-w-0">
                        <div
                          className="flex items-center justify-center w-6 h-6 rounded-md shrink-0"
                          style={{
                            background:
                              'color-mix(in srgb, var(--color-4-nuance-1) 12%, transparent)',
                          }}
                        >
                          <User className="w-3 h-3 text-[var(--color-4-nuance-1)]" />
                        </div>
                        <span className="text-sm text-foreground truncate">{p.name}</span>
                        {p.role === 'coordinator' && (
                          <LabelBadge
                            label="Coord."
                            variant="outlined"
                            color="var(--color-4-nuance-1)"
                            className="min-w-0 shrink-0"
                          />
                        )}
                        {p.role === 'volunteer' && (
                          <LabelBadge
                            label={getRoleLabel('volunteer')}
                            variant="outlined"
                            color="var(--color-3-nuance-1)"
                            className="min-w-0 shrink-0"
                          />
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="p-3">
                <p className="text-xs font-semibold text-[var(--color-3-nuance-1)] mb-2 flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  Élèves
                  <span className="ml-auto text-[10px] bg-[color-mix(in_srgb,var(--color-3-nuance-1)_15%,transparent)] text-[var(--color-3-nuance-1)] rounded-full px-1.5 py-0.5 font-bold">
                    {students.length + unregisteredCount}
                  </span>
                </p>
                {students.length === 0 && unregisteredCount === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Aucun</p>
                ) : (
                  <ul className="space-y-1.5">
                    {students.map((p) => (
                      <li key={`stu-${p.name}`} className="flex items-center gap-1.5 min-w-0">
                        <div
                          className="flex items-center justify-center w-6 h-6 rounded-md shrink-0"
                          style={{
                            background:
                              'color-mix(in srgb, var(--color-3-nuance-1) 12%, transparent)',
                          }}
                        >
                          <User className="w-3 h-3 text-[var(--color-3-nuance-1)]" />
                        </div>
                        <span className="text-sm text-foreground truncate flex-1 min-w-0">
                          {p.name}
                        </span>
                      </li>
                    ))}
                    {unregisteredCount > 0 && (
                      <li className="flex items-center gap-1.5 min-w-0">
                        <div
                          className="flex items-center justify-center w-6 h-6 rounded-md shrink-0"
                          style={{
                            background: 'color-mix(in srgb, var(--muted) 30%, transparent)',
                          }}
                        >
                          <Users className="w-3 h-3 text-muted-foreground" />
                        </div>
                        <span className="text-sm text-muted-foreground truncate flex-1 min-w-0">
                          +{unregisteredCount} non inscrit{unregisteredCount > 1 ? 's' : ''}
                        </span>
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {currentEvent.description && (
          <div className="p-3 rounded-xl bg-muted/20 border calendar-event-modal-section-border-c4">
            <p className="text-xs font-medium mb-1.5 calendar-event-modal-section-label-c4">
              Notes internes
            </p>
            <p className="text-sm text-foreground">{currentEvent.description}</p>
          </div>
        )}
      </div>
    );
  };

  /** Post-it body: priority / status / thematic, deadline, body, creator. */
  const renderPostItDetails = () => {
    const statusConfig = getPostitStatusChip(currentEvent.metadata.postitStatus);
    const StatusIcon = statusConfig.icon;
    const thematicLabel = getThematicLabel(currentEvent.metadata.postitThematic);
    const hasPriority = currentEvent.metadata.postitPriority;

    return (
      <div className="space-y-5">
        <div className="flex flex-wrap gap-3">
          {hasPriority && (
            <LabelBadge
              label="Prioritaire"
              icon={Star}
              variant="solid"
              color="var(--color-1-nuance-1)"
              className="min-w-0"
            />
          )}

          <LabelBadge
            label={statusConfig.label}
            icon={StatusIcon as LucideIcon}
            variant="outlined"
            color="var(--color-3-nuance-1)"
            className="min-w-0"
          />

          <LabelBadge
            label={thematicLabel}
            icon={Tag}
            variant="outlined"
            color="var(--color-2-nuance-1)"
            className="min-w-0"
          />
        </div>

        <div className="flex items-start gap-4 p-4 rounded-xl bg-muted/30 border calendar-event-modal-info-box-c3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg calendar-event-modal-icon-box-c3-n1">
            <Clock className="w-5 h-5" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Deadline</p>
            <p className="font-semibold text-foreground">
              {format(currentEvent.startDate, 'EEEE d MMMM yyyy', { locale: fr })}
            </p>
            <p className="text-muted-foreground">{format(currentEvent.startDate, 'HH:mm')}</p>
          </div>
        </div>

        {currentEvent.description && (
          <div className="p-4 rounded-xl bg-muted/20 border calendar-event-modal-section-border-c3">
            <p className="text-sm font-medium mb-2 calendar-event-modal-section-label-c3">
              Contenu
            </p>
            <p className="text-foreground whitespace-pre-wrap">{currentEvent.description}</p>
          </div>
        )}

        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-muted/20 border calendar-event-modal-creator-border-c3">
          <User className="w-5 h-5" />
          <span className="text-sm">
            <span className="text-muted-foreground">Créé par </span>
            <span className="font-medium text-foreground">
              {currentEvent.metadata.postitCreator}
            </span>
          </span>
        </div>
      </div>
    );
  };

  /** Holiday body: type / recurring / zone chips and the start–end timeline. */
  const renderHolidayDetails = () => {
    const holidayTypeLabel = getHolidayTypeLabel(currentEvent.metadata.holidayType);

    return (
      <div className="space-y-5">
        <div className="flex flex-wrap gap-3">
          <LabelBadge
            label={holidayTypeLabel}
            icon={typeIcons.holiday}
            variant="outlined"
            color="var(--color-2-nuance-1)"
            className="min-w-0"
          />
          {currentEvent.metadata.holidayRecurring && (
            <LabelBadge
              label="Récurrent"
              icon={Calendar}
              variant="outlined"
              color="var(--color-2-nuance-2)"
              className="min-w-0"
            />
          )}
          {currentEvent.metadata.vacationZoneCode && (
            <LabelBadge
              label={`Zone ${currentEvent.metadata.vacationZoneCode}`}
              icon={MapPin}
              variant="outlined"
              color="var(--color-2-nuance-1)"
              className="min-w-0"
            />
          )}
        </div>

        <div className="p-6 rounded-xl bg-muted/30 border calendar-event-modal-info-box-c1">
          <p className="calendar-event-modal-period-title">Période</p>
          <div className="calendar-event-modal-timeline-wrap">
            <div className="calendar-event-modal-timeline-line" />
            <div className="relative mb-14">
              <div className="calendar-event-modal-timeline-icon-start">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Début</p>
                <p className="text-base font-semibold text-foreground">
                  {format(currentEvent.startDate, 'd MMMM yyyy', { locale: fr })}
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="calendar-event-modal-timeline-icon-end">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Fin</p>
                <p className="text-base font-semibold text-foreground">
                  {format(currentEvent.endDate, 'd MMMM yyyy', { locale: fr })}
                </p>
              </div>
            </div>
          </div>
        </div>

        {currentEvent.description && (
          <div className="p-4 rounded-xl bg-muted/20 border calendar-event-modal-section-border-c1">
            <p className="text-sm font-medium mb-2 calendar-event-modal-section-label-c1">
              Description
            </p>
            <p className="text-foreground">{currentEvent.description}</p>
          </div>
        )}
      </div>
    );
  };

  /** Holidays are read-only; sessions / events navigate, post-its use `onEdit`. */
  const canEdit =
    currentEvent.type !== 'holiday' && (currentEvent.type !== 'event' || showFieldEvents);

  /** Deep-link to the source module, or hand the post-it back to the calendar page. */
  const handleEdit = () => {
    if (currentEvent.type === 'session') {
      void navigate(`/sessions?edit=${currentEvent.sourceId}`);
      onClose();
    } else if (currentEvent.type === 'event' && showFieldEvents) {
      void navigate(`/events?edit=${currentEvent.sourceId}`);
      onClose();
    } else if (currentEvent.type === 'postit') {
      if (onEdit) {
        onEdit(currentEvent.sourceId, currentEvent.type);
      }
      onClose();
    }
  };

  const actions = [
    {
      id: 'close',
      label: 'Fermer',
      onClick: onClose,
      preset: 'modal-cancel' as const,
    },
    ...(canEdit
      ? [
          {
            id: 'edit',
            label: 'Modifier',
            onClick: handleEdit,
            preset: 'modal-edit' as const,
          },
        ]
      : []),
  ];

  /** Header icon color keyed by calendar event type. */
  const getIconColor = () => {
    switch (currentEvent.type) {
      case 'session':
        return 'var(--color-2-nuance-1)';
      case 'event':
        return 'var(--color-4-nuance-1)';
      case 'postit':
        return 'var(--color-1-nuance-1)';
      case 'holiday':
        return 'var(--color-3-nuance-1)';
      default:
        return 'var(--muted-foreground)';
    }
  };

  /** Header icon wash keyed by calendar event type. */
  const getIconBgColor = () => {
    switch (currentEvent.type) {
      case 'session':
        return 'color-mix(in srgb, var(--color-2-nuance-1) 15%, transparent)';
      case 'event':
        return 'color-mix(in srgb, var(--color-4-nuance-1) 15%, transparent)';
      case 'postit':
        return 'color-mix(in srgb, var(--color-1-nuance-1) 15%, transparent)';
      case 'holiday':
        return 'color-mix(in srgb, var(--color-3-nuance-1) 15%, transparent)';
      default:
        return 'color-mix(in srgb, var(--muted) 50%, transparent)';
    }
  };

  return (
    <InfoModal
      open={isOpen}
      onClose={onClose}
      size="lg"
      icon={EventTypeIcon}
      iconColor={getIconColor()}
      iconBgColor={getIconBgColor()}
      iconSize="lg"
      title={currentEvent.title}
      titleColor="var(--foreground)"
      titleSize="xl"
      actions={actions}
      dataEventType={currentEvent.type}
    >
      {canGoBack && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          icon={ArrowLeft}
          className="mb-4 text-[var(--color-2-nuance-1)]"
        >
          Retour à la liste
        </Button>
      )}
      {currentEvent.type === 'session' && renderSessionDetails()}
      {currentEvent.type === 'event' && showFieldEvents && renderEventDetails()}
      {currentEvent.type === 'postit' && renderPostItDetails()}
      {currentEvent.type === 'holiday' && renderHolidayDetails()}
    </InfoModal>
  );
};
